import styled from '@emotion/styled';
import { ChangeEvent, ClassAttributes, createElement } from 'react';
import ViewportObserver from 'viewport-observer';

import { Offer } from '../../../service/persistent/offer/OfferWebApi';
import _OfferList, { OfferListItem } from '../../component/compound/OfferList';
import { OfferListSwitch } from '../../component/compound/OfferListFlagSwitch';
import OfferPortalWorkflow from '../../component/compound/OfferPortalWorkflow';
import _Breadcrumbs from '../../connected/Breadcrumbs';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutItem } from '../../styleUtility/gridLayout';

interface Props {
  offers: Offer[];
  loading?: boolean;
  hasNextPage: boolean;
  onRequestLoadMore: () => void;
  onChangeFlag: (e: ChangeEvent<HTMLElement>, isHideClosedOffer: boolean) => void;
  language: string;
  className?: string;
  isHideClosedOffer: boolean;
}

const OffersListPage = ({
  className,
  offers,
  loading = false,
  hasNextPage,
  onRequestLoadMore,
  onChangeFlag,
  language,
  isHideClosedOffer,
}: ClassAttributes<HTMLElement> & Props) => {
  const projectList = offers.map((offer) => {
    return <OfferListItem key={offer.id} offer={offer} />;
  });

  return (
    <Root className={className}>
      <Breadcrumbs />

      {language === 'ja-JP' ? (
        <Onboarding>
          <OfferPortalWorkflow />
        </Onboarding>
      ) : null}

      <OfferListSwitch
        onChangeEvent={onChangeFlag}
        isHideClosedOffer={!isHideClosedOffer}
        isDisabled={loading}
        messageId="offer_list_page.offer_uninclude_closed_switch"
      />

      <OfferList loading={loading}>{projectList}</OfferList>

      {!loading && projectList.length > 0 && hasNextPage && (
        <ViewportObserver onEnter={onRequestLoadMore}>
          {/* MS Edge has a bug around intersection-observer with 0-area element. This filler has 1px height to avoid that. */}
          {/* See https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/17319665/ */}
          <ViewPortObserverFiller />
        </ViewportObserver>
      )}
    </Root>
  );
};

const Root = styled('div')({
  ...flexibleLayoutContainer({
    direction: 'column',
    alignItems: 'stretch',
  }),
  margin: `0 ${SPACING_UNIT}px`,
  padding: `${SPACING_UNIT * 5}px calc((100% - 960px) / 2) ${SPACING_UNIT * 10}px`,
});

const Breadcrumbs = styled(_Breadcrumbs)({ marginBottom: SPACING_UNIT * 4 });

const Onboarding = styled('div')({
  ...gridLayoutItem('onboarding'),
  marginBottom: SPACING_REGULAR,
});

const OfferList = styled(_OfferList)({ marginBottom: SPACING_REGULAR });

const ViewPortObserverFiller = styled('div')({
  height: 1,
});

export default OffersListPage;
